var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { Box, Checkbox, Stack, Switch, Typography } from '@mui/material';
import { baseColors, colors } from '../../../../Themes/colors';
export var portalColors = {
    navy: baseColors.bluePreset[10],
    teal: baseColors.successPreset[30],
    tealDark: baseColors.successPreset[10],
    tealSoft: baseColors.successPreset[60],
    tealText: baseColors.successPreset[50],
    amberSoft: baseColors.warningPreset[30],
    amberText: colors.yellowShades[10],
    border: baseColors.greyPreset[60],
    surface: baseColors.fullWhite,
    subtle: baseColors.greyPreset[50],
    tile: baseColors.greyPreset[55],
    muted: baseColors.greyPreset[5],
    text: baseColors.darkPreset[10],
    danger: baseColors.errorPreset[20],
    pageBg: baseColors.successPreset[20],
};
export var brandButtonSx = {
    textTransform: 'none',
    backgroundColor: portalColors.teal,
    color: baseColors.fullWhite,
    '&:hover': { backgroundColor: portalColors.tealDark },
    '&.Mui-disabled': { backgroundColor: baseColors.greyPreset[20], color: baseColors.greyPreset[30] },
};
export var brandSwitchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: portalColors.teal },
    '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': { color: portalColors.teal },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: portalColors.teal, opacity: 0.5 },
    '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled .MuiSwitch-thumb': { color: portalColors.teal, opacity: 0.55 },
    '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track': { backgroundColor: portalColors.teal, opacity: 0.3 },
};
export var brandCheckboxSx = {
    color: baseColors.greyPreset[30],
    '&.Mui-checked': { color: portalColors.teal },
    '&.Mui-disabled': { color: baseColors.greyPreset[20] },
    '&.Mui-checked.Mui-disabled': { color: portalColors.teal, opacity: 0.55 },
};
export var SectionCard = function (_a) {
    var icon = _a.icon, title = _a.title, subtitle = _a.subtitle, badge = _a.badge, required = _a.required, error = _a.error, flush = _a.flush, children = _a.children;
    return (React.createElement(Box, { "data-invalid": error ? 'true' : undefined, sx: { background: portalColors.surface, border: "1px solid ".concat(error ? portalColors.danger : portalColors.border), borderRadius: '12px', mb: 2 } },
        React.createElement(Stack, { direction: "row", alignItems: "center", gap: 1.5, sx: { px: 2.5, py: 2, borderBottom: "1px solid ".concat(portalColors.border) } },
            React.createElement(Box, { sx: { width: 36, height: 36, borderRadius: '8px', background: portalColors.tile, display: 'grid', placeItems: 'center', color: portalColors.muted } }, icon),
            React.createElement(Box, { flex: 1 },
                React.createElement(Typography, { sx: { fontWeight: 600, fontSize: 16, color: portalColors.text } },
                    title,
                    required && React.createElement(Box, { component: "span", sx: { color: portalColors.danger } }, " *")),
                subtitle && React.createElement(Typography, { sx: { fontSize: 13, color: portalColors.muted } }, subtitle)),
            badge),
        React.createElement(Box, { sx: { p: flush ? 0 : 2.5 } },
            children,
            error && (React.createElement(Typography, { role: "alert", sx: __assign({ fontSize: 12, color: portalColors.danger }, (flush ? { px: 2.5, py: 1.25 } : { mt: 1 })) }, error)))));
};
export var Badge = function (_a) {
    var children = _a.children, _b = _a.tone, tone = _b === void 0 ? 'teal' : _b;
    var tones = {
        teal: { bg: portalColors.tealSoft, fg: portalColors.tealText },
        amber: { bg: portalColors.amberSoft, fg: portalColors.amberText },
        grey: { bg: portalColors.tile, fg: portalColors.muted },
    }[tone];
    return (React.createElement(Box, { component: "span", sx: { background: tones.bg, color: tones.fg, fontSize: 12, fontWeight: 600, px: 1.25, py: 0.25, borderRadius: '999px', whiteSpace: 'nowrap' } }, children));
};
var tileSx = {
    border: "1px solid ".concat(portalColors.border),
    borderRadius: '8px',
    background: portalColors.subtle,
    px: 2,
    py: 1.25,
    minHeight: 56,
};
export var ToggleTile = function (_a) {
    var name = _a.name, label = _a.label, checked = _a.checked, editing = _a.editing, onChange = _a.onChange;
    return (React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", gap: 1.5, sx: __assign(__assign({}, tileSx), { width: '100%' }) },
        React.createElement(Typography, { sx: { fontSize: 14, fontWeight: 500, color: portalColors.text } }, label),
        React.createElement(Switch, { checked: checked, disabled: !editing, onChange: function (event) { return onChange(name, event.target.checked); }, inputProps: { 'aria-label': label }, sx: brandSwitchSx })));
};
export var ApplicationCard = function (_a) {
    var name = _a.name, title = _a.title, description = _a.description, checked = _a.checked, editing = _a.editing, onChange = _a.onChange;
    return (React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", gap: 1.5, onClick: function () { return editing && onChange(name, !checked); }, sx: __assign(__assign({}, tileSx), { width: '100%', cursor: editing ? 'pointer' : 'default' }) },
        React.createElement(Box, null,
            React.createElement(Typography, { sx: { fontSize: 14, fontWeight: 600, color: portalColors.text } }, title),
            React.createElement(Typography, { sx: { fontSize: 12, color: portalColors.muted } }, description)),
        React.createElement(Checkbox, { checked: checked, disabled: !editing, onClick: function (event) { return event.stopPropagation(); }, onChange: function (event) { return onChange(name, event.target.checked); }, inputProps: { 'aria-label': title }, sx: brandCheckboxSx })));
};
export var DependentPanel = function (_a) {
    var label = _a.label, helper = _a.helper, required = _a.required, invalid = _a.invalid, children = _a.children;
    return (React.createElement(Box, { "data-invalid": invalid ? 'true' : undefined, sx: { mt: 1.5, px: 2, py: 1.25, background: portalColors.subtle, border: "1px solid ".concat(invalid ? portalColors.danger : portalColors.border), borderRadius: '8px' } },
        React.createElement(Typography, { sx: { fontSize: 11, fontWeight: 600, letterSpacing: 0.6, color: portalColors.muted, textTransform: 'uppercase', mb: 0.75 } },
            "\u21B3 ",
            label,
            required && React.createElement(Box, { component: "span", sx: { color: portalColors.danger } }, " *")),
        children,
        helper && React.createElement(Typography, { sx: { fontSize: 12, color: portalColors.muted, mt: 0.75 } }, helper)));
};
export var ReadValue = function (_a) {
    var children = _a.children, icon = _a.icon;
    return (React.createElement(Box, { sx: { display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, background: portalColors.surface, border: "1px solid ".concat(portalColors.border), borderRadius: '6px', fontSize: 14, fontWeight: 500 } },
        icon,
        children));
};
export var TileGrid = function (_a) {
    var children = _a.children, _b = _a.width, width = _b === void 0 ? 250 : _b;
    return (React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: "repeat(auto-fill, minmax(min(".concat(width, "px, 100%), ").concat(width, "px))"), gap: 1.5 } }, children));
};
